// Copyright (C) Microsoft Corporation. All rights reserved.

/**
 * Implements a tab control that allows for switching between different content sections.
 * Assumes a parent container with a single descendant element with ARIA role "tablist" with descendants
 * with ARIA role "tab" that have "aria-controls" attribute pointing to the content sections.
 */
export class TabControl {
	private static _tabSelector = '[role="tab"]';
	private _tabButtonContainer: HTMLElement;
	private _tabButtonContainerStyles: CSSStyleDeclaration;

	constructor(container: Element) {
		this._tabButtonContainer = container.querySelector('[role="tablist"]');

		if (!this._tabButtonContainer) {
			throw new Error('TabControl: tablist element missing');
		}

		this._tabButtonContainerStyles = getComputedStyle(this._tabButtonContainer);

		this._tabButtonContainer.addEventListener('keydown',
			event => {
				this._onKeyDown(event);
			});

		this._tabButtonContainer.addEventListener('click',
			event => {
				this._onClick(event);
			},
			{
				passive: false,
			});
	}

	private _onKeyDown(event: KeyboardEvent): void {
		if (event.target instanceof HTMLElement && event.target.matches(TabControl._tabSelector)) {
			let handled = false;

			switch (event.key) {
				case "ArrowLeft":
					handled = this._moveTabFocus(event.target,
						this._tabButtonContainerStyles.direction == "ltr");
					break;

				case "ArrowRight":
					handled = this._moveTabFocus(event.target,
						this._tabButtonContainerStyles.direction == "rtl");
					break;
			}

			if (handled) {
				event.preventDefault();
				event.stopPropagation();
			}
		}
	}

	private _onClick(event: MouseEvent): void {
		if (event.target instanceof Element) {
			if (event.target.matches(TabControl._tabSelector)) {
				event.preventDefault();
				this._activateTab(event.target);
			}
			else {
				const tabButton = event.target.querySelector(TabControl._tabSelector);
				if (tabButton) {
					event.preventDefault();
					this._activateTab(tabButton);
				}
			}
		}
	}

	private _activateTab(tabButton: Element): void {
		const tabs = Array.from(this._tabButtonContainer.querySelectorAll<HTMLElement>(TabControl._tabSelector));

		for (const button of tabs) {
			TabControl._setTabVisibility(button, button !== tabButton);
		}
	}

	private _moveTabFocus(currentTab: HTMLElement, previous: boolean): boolean {
		const tabs = Array.from(this._tabButtonContainer.querySelectorAll<HTMLElement>(TabControl._tabSelector));
		const currentTabIndex = tabs.indexOf(currentTab);

		if (currentTabIndex === -1) {
			return false;
		}

		const focusTabIndex = (currentTabIndex + tabs.length) + (previous ? -1 : 1);
		const focusTab = tabs[focusTabIndex % tabs.length];
		focusTab.focus();
		return true;
	}

	private static _setTabVisibility(tabButton: HTMLElement, hidden: boolean): void {
		const ariaHidden = String(hidden);

		// if aria-selected is same as desired hidden state,
		// then aria-selected and hidden state needs to be toggled
		if ((tabButton.ariaSelected || 'false') === ariaHidden) {
			for (const element of tabButton.ariaControlsElements) {
				element.ariaHidden = ariaHidden;
				if (element instanceof HTMLElement) {
					element.hidden = hidden;
				}
			}

			tabButton.ariaSelected = String(!hidden);
			tabButton.tabIndex = hidden ? -1 : 0;
		}
	}
}
